import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { renderRichText } from "../richText";
import { TypingDots } from "./TypingDots";
import { ConsentNotice } from "./ConsentNotice";
import { EmojiMartPicker } from "./EmojiMartPicker";
import { DEFAULT_AGENT_IMAGE, resolveWidgetAssetUrl } from "../config";
const PRODUCT_WEBSITE_PREVIEW_TEMPLATES = [
    {
        gradient: "linear-gradient(135deg, #06b6d4 0%, #3b82f6 48%, #4f46e5 100%)",
        glow: "rgba(103,232,249,0.5)",
        badge: "Website exclusive",
        title: "See the full details",
        iconPath: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0 0c2.2-2.4 3.4-5.4 3.4-9S14.2 5.4 12 3m0 18c-2.2-2.4-3.4-5.4-3.4-9S9.8 5.4 12 3M3.6 9h16.8M3.6 15h16.8",
    },
    {
        gradient: "linear-gradient(135deg, #d946ef 0%, #8b5cf6 48%, #4f46e5 100%)",
        glow: "rgba(240,171,252,0.5)",
        badge: "More details online",
        title: "Discover it on the website",
        iconPath: "m12 3-1.7 5.3L5 10l5.3 1.7L12 17l1.7-5.3L19 10l-5.3-1.7L12 3Zm-6 12-.8 2.2L3 18l2.2.8L6 21l.8-2.2L9 18l-2.2-.8L6 15Z",
    },
    {
        gradient: "linear-gradient(135deg, #10b981 0%, #14b8a6 48%, #0891b2 100%)",
        glow: "rgba(110,231,183,0.5)",
        badge: "Explore online",
        title: "View the complete offer",
        iconPath: "m16.5 7.5-3 6-6 3 3-6 6-3ZM12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z",
    },
    {
        gradient: "linear-gradient(135deg, #f97316 0%, #f43f5e 48%, #db2777 100%)",
        glow: "rgba(253,186,116,0.5)",
        badge: "Full info on website",
        title: "Open complete details",
        iconPath: "m4 7 8-4 8 4-8 4-8-4Zm0 0v10l8 4 8-4V7M12 11v10",
    },
    {
        gradient: "linear-gradient(135deg, #8b5cf6 0%, #3b82f6 48%, #0ea5e9 100%)",
        glow: "rgba(196,181,253,0.5)",
        badge: "Available online",
        title: "Visit the website",
        iconPath: "M14 5h5v5m0-5-8 8M18 13v5a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5",
    },
    {
        gradient: "linear-gradient(135deg, #fbbf24 0%, #f97316 48%, #ef4444 100%)",
        glow: "rgba(253,230,138,0.55)",
        badge: "See more online",
        title: "Get the full experience",
        iconPath: "M14.5 5.5c2.2-2.2 4.4-2.4 5.5-2.5-.1 1.1-.3 3.3-2.5 5.5L13 13l-4-4 5.5-3.5ZM9 9l-4.5.5L3 11l4 2m4 4 2 4 1.5-1.5L15 15m-7 1-2 2",
    },
];
export function ChatView({ brandName, agentImage, theme, t, messages, input, onInputChange, onSend, flowBadges, showFlowBadges, onPickFlow, messageSuggestions, onPickSuggestion, loading, activityLabel, streamingId, inputDisabled, onRetryLast, retryDisabled, showConsent, onApproveConsent, onConfirmBooking, }) {
    const isDarkColor = (value) => {
        if (!value)
            return false;
        const color = value.trim().toLowerCase();
        const hex = color.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
        if (hex) {
            const raw = hex[1];
            const full = raw.length === 3 ? raw.split("").map((ch) => `${ch}${ch}`).join("") : raw;
            const r = parseInt(full.slice(0, 2), 16);
            const g = parseInt(full.slice(2, 4), 16);
            const b = parseInt(full.slice(4, 6), 16);
            return (r * 299 + g * 587 + b * 114) / 1000 < 140;
        }
        const rgb = color.match(/^rgba?\(([^)]+)\)$/);
        if (rgb) {
            const [r, g, b] = rgb[1].split(",").map((part) => Number(part.trim())).slice(0, 3);
            if ([r, g, b].every((channel) => Number.isFinite(channel))) {
                return (r * 299 + g * 587 + b * 114) / 1000 < 140;
            }
        }
        return false;
    };
    const scrollerRef = useRef(null);
    const inputRef = useRef(null);
    const sourcePopoverCloseTimerRef = useRef(null);
    const textColor = theme.primaryTextColor || theme.contentColor || theme.foreground;
    const assistantTextColor = theme.assistantTextColor || textColor;
    const mutedColor = theme.secondaryTextColor || "#64748b";
    const cardBackground = theme.cardBackgroundColor || theme.secondaryBackground || "#ffffff";
    const darkSurface = isDarkColor(theme.background);
    const itemFallbackLogoUrl = resolveWidgetAssetUrl(agentImage);
    const chipShadowIdle = darkSurface ? "0 5px 12px rgba(0,0,0,0.4)" : "0 5px 12px #e2e8f0";
    const chipShadowHover = darkSurface ? "0 10px 20px rgba(0,0,0,0.5)" : "0 10px 20px #dbe3ee";
    const inputShadow = darkSurface ? "0 6px 16px rgba(0,0,0,0.42)" : "0 6px 16px #dbe3ee";
    const surface = cardBackground;
    const errorAccent = "rgba(248,113,113,1)";
    const errorBorder = "rgba(248,113,113,0.55)";
    const errorBackground = "rgba(248,113,113,0.12)";
    const errorText = "rgba(252,165,165,0.95)";
    const [calendarSelections, setCalendarSelections] = useState({});
    const [expandedSources, setExpandedSources] = useState({});
    const [hoveredSourceGroup, setHoveredSourceGroup] = useState(null);
    const [emojiOpen, setEmojiOpen] = useState(false);
    const [hoveredFlowSuggestion, setHoveredFlowSuggestion] = useState(null);
    const [hoveredSuggestion, setHoveredSuggestion] = useState(null);
    const [expandedProductsMessageId, setExpandedProductsMessageId] = useState(null);
    const emojiWrapRef = useRef(null);
    useEffect(() => {
        return () => {
            if (sourcePopoverCloseTimerRef.current != null && typeof window !== "undefined") {
                window.clearTimeout(sourcePopoverCloseTimerRef.current);
            }
        };
    }, []);
    useEffect(() => {
        if (!emojiOpen)
            return;
        const onClickOutside = (event) => {
            if (!emojiWrapRef.current)
                return;
            if (!emojiWrapRef.current.contains(event.target)) {
                setEmojiOpen(false);
            }
        };
        document.addEventListener("mousedown", onClickOutside);
        return () => document.removeEventListener("mousedown", onClickOutside);
    }, [emojiOpen]);
    const formatDayLabel = useMemo(() => (date, timezone) => new Intl.DateTimeFormat("en-US", {
        timeZone: timezone,
        weekday: "short",
        month: "short",
        day: "numeric",
    }).format(date), []);
    const formatDayKey = useMemo(() => (date, timezone) => new Intl.DateTimeFormat("en-CA", {
        timeZone: timezone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).format(date), []);
    const formatSlotTime = useMemo(() => (iso, timezone) => new Intl.DateTimeFormat("en-US", {
        timeZone: timezone,
        hour: "numeric",
        minute: "2-digit",
    }).format(new Date(iso)), []);
    useEffect(() => {
        if (scrollerRef.current) {
            scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
        }
    }, [messages, loading, streamingId, showFlowBadges, flowBadges]);
    const renderCalendar = (message) => {
        var _a, _b;
        if (!message.calendar)
            return null;
        const timezone = message.calendar.timezone || "UTC";
        const grouped = message.calendar.slots.reduce((acc, slot) => {
            const date = new Date(slot.start);
            const dayKey = formatDayKey(date, timezone);
            if (!acc[dayKey]) {
                acc[dayKey] = { label: formatDayLabel(date, timezone), slots: [] };
            }
            acc[dayKey].slots.push(slot);
            return acc;
        }, {});
        Object.values(grouped).forEach((group) => {
            group.slots.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
        });
        const dayKeys = Object.keys(grouped).sort();
        const selection = calendarSelections[message.id];
        const activeDayKey = (selection === null || selection === void 0 ? void 0 : selection.dayKey) || dayKeys[0];
        const slots = activeDayKey ? ((_a = grouped[activeDayKey]) === null || _a === void 0 ? void 0 : _a.slots) || [] : [];
        const selectedSlotKey = selection === null || selection === void 0 ? void 0 : selection.slotKey;
        const selectedSlot = slots.find((slot) => slot.start === selectedSlotKey);
        if (!dayKeys.length) {
            return _jsx("div", { style: { fontSize: 13, color: mutedColor }, children: t("chat.noAvailableTimes") });
        }
        return (_jsxs("div", { style: { marginTop: 8, display: "grid", gap: 10 }, children: [_jsx("div", { style: {
                        display: "flex",
                        gap: 8,
                        overflowX: "auto",
                        paddingBottom: 4,
                    }, children: dayKeys.map((dayKey) => {
                        var _a, _b, _c;
                        const isActive = dayKey === activeDayKey;
                        return (_jsxs("button", { type: "button", onClick: () => setCalendarSelections((prev) => ({
                                ...prev,
                                [message.id]: { dayKey, slotKey: "" },
                            })), style: {
                                borderRadius: 12,
                                border: `1px solid ${isActive ? theme.accentColor : "rgba(255,255,255,0.12)"}`,
                                background: isActive ? "rgba(255,255,255,0.08)" : surface,
                                color: textColor,
                                padding: "8px 10px",
                                fontSize: 12,
                                minWidth: 110,
                                textAlign: "left",
                                cursor: "pointer",
                            }, children: [_jsx("div", { style: { fontWeight: 600 }, children: (_a = grouped[dayKey]) === null || _a === void 0 ? void 0 : _a.label }), _jsx("div", { style: { fontSize: 11, opacity: 0.7 }, children: t("chat.slots", { count: (_c = (_b = grouped[dayKey]) === null || _b === void 0 ? void 0 : _b.slots.length) !== null && _c !== void 0 ? _c : 0 }) })] }, dayKey));
                    }) }), _jsx("div", { style: { display: "grid", gap: 8 }, children: slots.map((slot) => {
                        const slotKey = slot.start;
                        const isSelected = slotKey === selectedSlotKey;
                        return (_jsxs("button", { type: "button", onClick: () => setCalendarSelections((prev) => ({
                                ...prev,
                                [message.id]: { dayKey: activeDayKey, slotKey },
                            })), style: {
                                borderRadius: 10,
                                border: `1px solid ${isSelected ? theme.accentColor : "rgba(255,255,255,0.12)"}`,
                                background: isSelected ? "rgba(56,189,248,0.2)" : surface,
                                color: textColor,
                                padding: "8px 10px",
                                fontSize: 13,
                                textAlign: "left",
                                cursor: "pointer",
                            }, children: [formatSlotTime(slot.start, timezone), " - ", formatSlotTime(slot.end, timezone)] }, slotKey));
                    }) }), message.bookingStatus === "confirmed" && message.bookingDetails ? (_jsx("div", { style: { fontSize: 12, color: "rgba(34,197,94,0.95)" }, children: t("chat.bookedFor", {
                        start: formatSlotTime(message.bookingDetails.start, timezone),
                        end: formatSlotTime(message.bookingDetails.end, timezone),
                    }) })) : message.bookingStatus === "failed" ? (_jsx("div", { style: { fontSize: 12, color: errorAccent }, children: t("chat.unableToConfirm") })) : null, _jsxs("div", { style: { display: "flex", flexDirection: "column", gap: 6 }, children: [_jsxs("div", { style: { fontSize: 11, color: mutedColor }, children: [t("chat.timezone", { timezone }), typeof ((_b = message.calendar) === null || _b === void 0 ? void 0 : _b.duration_minutes) === "number"
                                    ? ` · ${t("chat.durationMinutes", { minutes: message.calendar.duration_minutes })}`
                                    : ""] }), _jsx("button", { type: "button", onClick: () => selectedSlot && (onConfirmBooking === null || onConfirmBooking === void 0 ? void 0 : onConfirmBooking(message.id, selectedSlot)), disabled: !selectedSlot || message.bookingStatus === "pending" || message.bookingStatus === "confirmed", style: {
                                borderRadius: 9999,
                                border: `1px solid ${theme.accentColor}`,
                                padding: "8px 12px",
                                background: message.bookingStatus === "pending"
                                    ? "rgba(56,189,248,0.15)"
                                    : message.bookingStatus === "confirmed"
                                        ? "rgba(34,197,94,0.2)"
                                        : theme.accentColor,
                                color: "#0f172a",
                                fontSize: 12,
                                fontWeight: 600,
                                cursor: !selectedSlot || message.bookingStatus === "pending" || message.bookingStatus === "confirmed"
                                    ? "not-allowed"
                                    : "pointer",
                                opacity: !selectedSlot ? 0.6 : 1,
                            }, children: message.bookingStatus === "pending"
                                ? t("chat.bookingPending")
                                : message.bookingStatus === "confirmed"
                                    ? t("chat.booked")
                                    : t("chat.confirmBooking") })] })] }));
    };
    const truncateProductDescription = (value) => {
        const text = (value || "").trim();
        if (!text)
            return "";
        const words = text.split(/\s+/);
        return words.length > 8 ? `${words.slice(0, 8).join(" ")}...` : text;
    };
    const renderProductCard = (product, compact = false, productIndex = 0) => {
        const websitePreview = PRODUCT_WEBSITE_PREVIEW_TEMPLATES[productIndex % PRODUCT_WEBSITE_PREVIEW_TEMPLATES.length];
        return (_jsxs("a", { href: product.product_url, target: "_blank", rel: "noopener noreferrer", style: {
                display: "grid",
                gap: 10,
                minWidth: compact ? 182 : 220,
                maxWidth: compact ? 182 : 240,
                textDecoration: "none",
                color: textColor,
                borderRadius: 16,
                border: "1px solid rgba(148,163,184,0.22)",
                background: cardBackground,
                padding: 10,
                boxShadow: darkSurface ? "0 12px 24px rgba(2,6,23,0.28)" : "0 12px 24px rgba(15,23,42,0.08)",
            }, children: [_jsx("div", { style: {
                        height: compact ? 116 : 138,
                        borderRadius: 12,
                        overflow: "hidden",
                        background: darkSurface ? "rgba(255,255,255,0.06)" : "#f8fafc",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }, children: product.main_image_url ? (_jsx("img", { src: product.main_image_url, alt: product.title, style: { width: "100%", height: "100%", objectFit: "cover" } })) : itemFallbackLogoUrl ? (_jsxs("div", { style: {
                            position: "relative",
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                            padding: 20,
                            boxSizing: "border-box",
                            background: darkSurface
                                ? "linear-gradient(135deg, rgba(15,23,42,0.98) 0%, rgba(30,41,59,0.96) 52%, rgba(15,23,42,0.98) 100%)"
                                : "linear-gradient(135deg, #f8fafc 0%, #ffffff 52%, #eef2f7 100%)",
                        }, children: [_jsx("div", { style: {
                                    position: "absolute",
                                    inset: 0,
                                    background: "radial-gradient(circle at center, rgba(59,130,246,0.13), transparent 58%)",
                                } }), _jsx("div", { style: {
                                    position: "relative",
                                    width: compact ? 76 : 88,
                                    height: compact ? 76 : 88,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    borderRadius: 18,
                                    border: darkSurface ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(255,255,255,0.85)",
                                    background: "rgba(255,255,255,0.94)",
                                    padding: 12,
                                    boxSizing: "border-box",
                                    boxShadow: darkSurface ? "0 16px 34px rgba(0,0,0,0.32)" : "0 16px 34px rgba(15,23,42,0.14)",
                                    backdropFilter: "blur(8px)",
                                }, children: _jsx("img", { src: itemFallbackLogoUrl, alt: "", loading: "lazy", style: { maxWidth: "100%", maxHeight: "100%", objectFit: "contain" } }) })] })) : (_jsxs("div", { style: {
                            position: "relative",
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            overflow: "hidden",
                            padding: 12,
                            boxSizing: "border-box",
                            color: "#ffffff",
                            background: websitePreview.gradient,
                        }, children: [_jsx("div", { style: {
                                    position: "absolute",
                                    width: 84,
                                    height: 84,
                                    borderRadius: "50%",
                                    top: -30,
                                    right: -22,
                                    background: websitePreview.glow,
                                    filter: "blur(22px)",
                                } }), _jsx("div", { style: {
                                    position: "absolute",
                                    width: 72,
                                    height: 72,
                                    borderRadius: "50%",
                                    bottom: -32,
                                    left: -20,
                                    background: "rgba(255,255,255,0.18)",
                                    filter: "blur(18px)",
                                } }), _jsxs("div", { style: { position: "relative", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }, children: [_jsx("span", { style: {
                                            width: 34,
                                            height: 34,
                                            flexShrink: 0,
                                            display: "grid",
                                            placeItems: "center",
                                            borderRadius: 11,
                                            border: "1px solid rgba(255,255,255,0.3)",
                                            background: "rgba(255,255,255,0.18)",
                                            boxShadow: "0 4px 10px rgba(15,23,42,0.12)",
                                            backdropFilter: "blur(8px)",
                                        }, children: _jsx("svg", { width: "17", height: "17", viewBox: "0 0 24 24", fill: "none", "aria-hidden": "true", children: _jsx("path", { d: websitePreview.iconPath, stroke: "currentColor", strokeWidth: "1.8", strokeLinecap: "round", strokeLinejoin: "round" }) }) }), _jsx("span", { style: {
                                            borderRadius: 9999,
                                            border: "1px solid rgba(255,255,255,0.24)",
                                            background: "rgba(255,255,255,0.14)",
                                            padding: "4px 6px",
                                            fontSize: 7,
                                            fontWeight: 800,
                                            letterSpacing: "0.1em",
                                            lineHeight: 1.2,
                                            textTransform: "uppercase",
                                            textAlign: "right",
                                            backdropFilter: "blur(8px)",
                                        }, children: websitePreview.badge })] }), _jsxs("div", { style: { position: "relative", display: "grid", gap: 4 }, children: [_jsx("span", { style: { fontSize: 12, fontWeight: 800, lineHeight: 1.15 }, children: websitePreview.title }), _jsxs("span", { style: { display: "inline-flex", alignItems: "center", gap: 4, fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.86)" }, children: ["View full info", _jsx("svg", { width: "10", height: "10", viewBox: "0 0 24 24", fill: "none", "aria-hidden": "true", children: _jsx("path", { d: "M14 5h5v5m0-5-8 8M18 13v5a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }) })] })] })] })) }), _jsxs("div", { style: { display: "grid", gap: 6 }, children: [_jsx("div", { style: { fontSize: 13, fontWeight: 700, lineHeight: 1.3 }, children: product.title }), product.price ? _jsx("div", { style: { fontSize: 13, fontWeight: 700, color: theme.accentColor }, children: product.price }) : null, product.description ? (_jsx("div", { style: { fontSize: 11, lineHeight: 1.45, color: mutedColor }, children: truncateProductDescription(product.description) })) : null] })] }, `${product.product_url}-${product.title}`));
    };
    const renderProducts = (message) => {
        var _a, _b;
        const recommended = ((_a = message.products) === null || _a === void 0 ? void 0 : _a.recommended) || [];
        const similar = ((_b = message.products) === null || _b === void 0 ? void 0 : _b.similar) || [];
        if (!recommended.length && !similar.length)
            return null;
        const visibleProducts = recommended.slice(0, 5);
        const remainingProducts = recommended.slice(5);
        const hasMoreProducts = remainingProducts.length > 0 || similar.length > 0;
        const allProducts = [...recommended, ...similar];
        return (_jsxs(_Fragment, { children: [_jsxs("div", { style: { marginTop: 12, display: "grid", gap: 10 }, children: [_jsx("div", { style: {
                                display: "flex",
                                gap: 10,
                                overflowX: "auto",
                                paddingBottom: 6,
                                scrollbarWidth: "thin",
                            }, children: visibleProducts.map((product, index) => renderProductCard(product, true, index)) }), hasMoreProducts ? (_jsx("button", { type: "button", onClick: () => setExpandedProductsMessageId(message.id), style: {
                                alignSelf: "center",
                                borderRadius: 9999,
                                border: "1px solid rgba(148,163,184,0.24)",
                                background: "rgba(255,255,255,0.04)",
                                color: textColor,
                                width: 34,
                                height: 34,
                                cursor: "pointer",
                                fontSize: 18,
                                lineHeight: 1,
                            }, children: "\u25BE" })) : null] }), expandedProductsMessageId === message.id ? (_jsx("div", { style: {
                        position: "fixed",
                        inset: 0,
                        background: "rgba(2,6,23,0.55)",
                        backdropFilter: "blur(6px)",
                        zIndex: 1200,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 16,
                    }, onClick: () => setExpandedProductsMessageId(null), children: _jsxs("div", { style: {
                            width: "min(960px, 100%)",
                            maxHeight: "82vh",
                            overflowY: "auto",
                            borderRadius: 24,
                            background: theme.background,
                            color: textColor,
                            border: "1px solid rgba(148,163,184,0.22)",
                            padding: 20,
                            boxShadow: darkSurface ? "0 28px 80px rgba(0,0,0,0.45)" : "0 28px 80px rgba(15,23,42,0.18)",
                        }, onClick: (event) => event.stopPropagation(), children: [_jsxs("div", { style: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }, children: [_jsxs("div", { children: [_jsx("div", { style: { fontSize: 12, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: mutedColor }, children: "Recommended matches" }), _jsx("div", { style: { marginTop: 6, fontSize: 20, fontWeight: 700 }, children: "Recommended options" })] }), _jsx("button", { type: "button", onClick: () => setExpandedProductsMessageId(null), style: {
                                            borderRadius: 9999,
                                            border: "1px solid rgba(148,163,184,0.22)",
                                            background: "transparent",
                                            color: textColor,
                                            width: 34,
                                            height: 34,
                                            cursor: "pointer",
                                        }, children: "\u00D7" })] }), _jsxs("div", { style: { marginTop: 18, display: "grid", gap: 24 }, children: [recommended.length ? (_jsxs("div", { style: { display: "grid", gap: 12 }, children: [_jsx("div", { style: { fontSize: 13, fontWeight: 700, color: mutedColor }, children: "Top recommendations" }), _jsx("div", { style: { display: "flex", flexWrap: "wrap", gap: 12 }, children: recommended.map((product, index) => renderProductCard(product, false, index)) })] })) : null, similar.length ? (_jsxs("div", { style: { display: "grid", gap: 12 }, children: [_jsx("div", { style: { fontSize: 13, fontWeight: 700, color: mutedColor }, children: "Similar options" }), _jsx("div", { style: { display: "flex", flexWrap: "wrap", gap: 12 }, children: similar.map((product, index) => renderProductCard(product, false, recommended.length + index)) })] })) : null, !allProducts.length ? _jsx("div", { style: { fontSize: 14, color: mutedColor }, children: "No recommendations available." }) : null] })] }) })) : null] }));
    };
    const renderHandoffConnectingCard = (message) => (_jsx("div", { style: {
            alignSelf: "center",
            width: "calc(100% - 16px)",
            maxWidth: 320,
            margin: "8px auto",
            borderRadius: 18,
            border: darkSurface ? "1px solid rgba(148,163,184,0.24)" : "1px solid rgba(148,163,184,0.28)",
            background: darkSurface
                ? "linear-gradient(135deg, rgba(15,23,42,0.98), rgba(30,41,59,0.96))"
                : "linear-gradient(135deg, #ffffff, #f8fafc)",
            boxShadow: darkSurface ? "0 16px 34px rgba(0,0,0,0.32)" : "0 16px 34px rgba(15,23,42,0.12)",
            padding: 14,
            color: textColor,
            position: "relative",
            boxSizing: "border-box",
        }, children: _jsxs("div", { style: {
                display: "grid",
                gridTemplateColumns: "44px minmax(0, 1fr)",
                gap: 12,
                alignItems: "center",
            }, children: [_jsx("div", { style: {
                        width: 44,
                        height: 44,
                        borderRadius: 15,
                        display: "grid",
                        placeItems: "center",
                        background: darkSurface ? "rgba(255,255,255,0.08)" : "rgba(124,58,237,0.08)",
                        color: "#ffffff",
                        border: darkSurface ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(124,58,237,0.12)",
                        boxSizing: "border-box",
                    }, children: _jsx("svg", { width: "22", height: "22", viewBox: "0 0 24 24", fill: "none", "aria-hidden": "true", style: { color: theme.accentColor }, children: _jsx("path", { d: "M7 10.8a3.8 3.8 0 1 0 0-7.6 3.8 3.8 0 0 0 0 7.6Zm10.2 1.2a3.4 3.4 0 1 0 0-6.8 3.4 3.4 0 0 0 0 6.8ZM2.5 20a5.1 5.1 0 0 1 8.8-3.5M13.4 20a4.7 4.7 0 0 1 8.1 0", stroke: "currentColor", strokeWidth: "1.8", strokeLinecap: "round", strokeLinejoin: "round" }) }) }), _jsxs("div", { style: { minWidth: 0, display: "grid", gap: 5 }, children: [_jsx("div", { style: { fontSize: 14, fontWeight: 800, lineHeight: 1.25, color: textColor }, children: t("widget.handoff.connectingTitle") }), _jsx("div", { style: { fontSize: 11.5, lineHeight: 1.45, color: mutedColor }, children: t("widget.handoff.connectingBody") })] })] }) }, message.id));
    const renderBubble = (message) => {
        if (message.messageType === "handoff_connecting") {
            return renderHandoffConnectingCard(message);
        }
        const isAssistant = message.role === "assistant" || message.role === "admin";
        const isError = Boolean(message.error);
        const showRetry = Boolean(isError && message.retryable && onRetryLast);
        const assistantAvatar = resolveWidgetAssetUrl(message.senderImage) ||
            resolveWidgetAssetUrl(agentImage) ||
            resolveWidgetAssetUrl(DEFAULT_AGENT_IMAGE) ||
            DEFAULT_AGENT_IMAGE;
        const messageSources = isAssistant && Array.isArray(message.sources) ? message.sources.filter((source) => /^https?:\/\//i.test(source)) : [];
        const groupedSources = (() => {
            const groups = new Map();
            for (const source of messageSources) {
                let origin = "invalid";
                let label = t("chat.invalidSourceGroup");
                try {
                    const parsed = new URL(source);
                    if ((parsed.protocol === "http:" || parsed.protocol === "https:") && parsed.hostname) {
                        origin = `${parsed.protocol}//${parsed.hostname}${parsed.port ? `:${parsed.port}` : ""}`;
                        label = parsed.hostname.replace(/^www\./i, "");
                    }
                }
                catch {
                    // Keep invalid source group
                }
                const existing = groups.get(origin) || { origin, label, urls: [] };
                if (!existing.urls.includes(source)) {
                    existing.urls.push(source);
                }
                groups.set(origin, existing);
            }
            return Array.from(groups.values()).sort((a, b) => {
                const byCount = b.urls.length - a.urls.length;
                if (byCount !== 0)
                    return byCount;
                return a.label.localeCompare(b.label);
            });
        })();
        const hasGroupedSources = groupedSources.length > 0;
        const sourcesExpanded = Boolean(expandedSources[message.id]);
        const openSourceGroup = (origin) => {
            if (sourcePopoverCloseTimerRef.current != null && typeof window !== "undefined") {
                window.clearTimeout(sourcePopoverCloseTimerRef.current);
                sourcePopoverCloseTimerRef.current = null;
            }
            setHoveredSourceGroup({ messageId: message.id, origin });
        };
        const scheduleCloseSourceGroup = () => {
            if (typeof window === "undefined")
                return;
            if (sourcePopoverCloseTimerRef.current != null) {
                window.clearTimeout(sourcePopoverCloseTimerRef.current);
            }
            sourcePopoverCloseTimerRef.current = window.setTimeout(() => {
                setHoveredSourceGroup(null);
                sourcePopoverCloseTimerRef.current = null;
            }, 160);
        };
        const messageBody = (_jsxs("div", { style: { fontSize: 14, lineHeight: 1.5 }, children: [message.content ? (isAssistant ? (_jsx("div", { style: { display: "grid", gap: 6 }, dangerouslySetInnerHTML: { __html: renderRichText(message.content) } })) : (message.content)) : message.streaming && !activityLabel ? (_jsx(TypingDots, { color: theme.accentColor })) : null, message.messageType === "calendar" ? renderCalendar(message) : null, isAssistant ? renderProducts(message) : null, hasGroupedSources ? (_jsxs("div", { style: { marginTop: 10, display: "grid", gap: 6 }, children: [_jsxs("button", { type: "button", onClick: () => {
                                setExpandedSources((prev) => ({ ...prev, [message.id]: !sourcesExpanded }));
                                setHoveredSourceGroup(null);
                            }, style: {
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                width: "fit-content",
                                border: "1px solid rgba(255,255,255,0.14)",
                                borderRadius: 9999,
                                background: "rgba(255,255,255,0.03)",
                                color: textColor,
                                padding: "4px 10px",
                                fontSize: 11,
                                cursor: "pointer",
                            }, children: [_jsx("span", { children: t("chat.sources") }), _jsx("span", { style: { display: "inline-block", transform: sourcesExpanded ? "rotate(180deg)" : "rotate(0deg)" }, children: "\u25BE" })] }), sourcesExpanded ? (_jsx("div", { style: { display: "flex", flexWrap: "wrap", gap: 6 }, children: groupedSources.map((group) => {
                                const isHovered = (hoveredSourceGroup === null || hoveredSourceGroup === void 0 ? void 0 : hoveredSourceGroup.messageId) === message.id && (hoveredSourceGroup === null || hoveredSourceGroup === void 0 ? void 0 : hoveredSourceGroup.origin) === group.origin;
                                return (_jsxs("div", { style: { position: "relative" }, onMouseEnter: () => openSourceGroup(group.origin), onMouseLeave: scheduleCloseSourceGroup, children: [_jsxs("div", { style: {
                                                borderRadius: 9999,
                                                border: "1px solid rgba(255,255,255,0.16)",
                                                background: "rgba(255,255,255,0.03)",
                                                color: textColor,
                                                padding: "4px 9px",
                                                fontSize: 11,
                                                cursor: "default",
                                                whiteSpace: "nowrap",
                                            }, children: [group.label, " (", group.urls.length, ")"] }), isHovered ? (_jsxs("div", { style: {
                                                position: "absolute",
                                                top: "100%",
                                                left: 0,
                                                minWidth: 280,
                                                maxWidth: 420,
                                                maxHeight: 220,
                                                overflowY: "auto",
                                                borderRadius: 10,
                                                border: "1px solid rgba(255,255,255,0.18)",
                                                background: "rgba(9,14,27,0.97)",
                                                padding: 8,
                                                zIndex: 25,
                                                boxShadow: "0 14px 30px rgba(0,0,0,0.35)",
                                                display: "grid",
                                                gap: 6,
                                            }, onMouseEnter: () => openSourceGroup(group.origin), onMouseLeave: scheduleCloseSourceGroup, children: [_jsx("div", { style: { fontSize: 11, color: mutedColor }, children: group.label }), group.urls.map((url) => (_jsx("a", { href: url, target: "_blank", rel: "noopener noreferrer", style: {
                                                        color: textColor,
                                                        fontSize: 12,
                                                        textDecoration: "none",
                                                        lineHeight: 1.4,
                                                        borderRadius: 6,
                                                        padding: "4px 6px",
                                                        background: "rgba(255,255,255,0.02)",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                    }, title: url, children: url }, `${message.id}-${group.origin}-${url}`)))] })) : null] }, `${message.id}-${group.origin}`));
                            }) })) : null] })) : null] }));
        if (isAssistant) {
            return (_jsx("div", { style: {
                    alignSelf: "flex-start",
                    maxWidth: "92%",
                    position: "relative",
                    color: isError ? errorText : assistantTextColor,
                }, children: _jsxs("div", { style: { display: "flex", alignItems: "flex-start", gap: 10 }, children: [_jsx("img", { src: assistantAvatar, alt: brandName, style: { width: 24, height: 24, borderRadius: "50%", objectFit: "cover", flexShrink: 0, marginTop: 2 } }), _jsxs("div", { style: { minWidth: 0, flex: 1 }, children: [messageBody, showRetry && (_jsxs("div", { style: { marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }, children: [_jsx("div", { style: { fontSize: 12, color: errorText, opacity: 0.9 }, children: t("chat.retryHint") }), _jsx("button", { type: "button", onClick: onRetryLast, disabled: retryDisabled, style: {
                                                alignSelf: "flex-start",
                                                borderRadius: 9999,
                                                border: `1px solid ${errorBorder}`,
                                                padding: "6px 12px",
                                                background: retryDisabled ? "rgba(248,113,113,0.08)" : "rgba(248,113,113,0.18)",
                                                color: errorAccent,
                                                fontSize: 12,
                                                fontWeight: 600,
                                                cursor: retryDisabled ? "not-allowed" : "pointer",
                                            }, children: t("chat.retry") })] }))] })] }) }, message.id));
        }
        return (_jsx("div", { style: {
                alignSelf: "flex-end",
                background: theme.userMessageBackgroundColor || "#2563eb",
                color: theme.userMessageTextColor || "#ffffff",
                padding: "10px 12px",
                borderRadius: `${theme.corner} ${theme.corner} 6px ${theme.corner}`,
                border: "none",
                maxWidth: "86%",
                position: "relative",
            }, children: messageBody }, message.id));
    };
    return (_jsxs("div", { style: { display: "flex", flexDirection: "column", height: "100%" }, children: [_jsxs("div", { ref: scrollerRef, style: {
                    flex: 1,
                    overflowY: "auto",
                    padding: 14,
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                    background: theme.activeChatBackgroundColor || theme.background || "#f4f7fb",
                }, children: [messages.map((m) => renderBubble(m)), showFlowBadges ? (_jsx("div", { style: {
                            alignSelf: "flex-end",
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 8,
                            justifyContent: "flex-end",
                            maxWidth: "82%",
                            marginLeft: "auto",
                            marginTop: 4,
                        }, children: flowBadges.map((flow) => (_jsx("button", { onClick: () => onPickFlow(flow), onMouseEnter: () => setHoveredFlowSuggestion(flow.id), onMouseLeave: () => setHoveredFlowSuggestion((prev) => (prev === flow.id ? null : prev)), style: {
                                borderRadius: 9999,
                                border: "1px solid #d8e0eb",
                                padding: "6px 10px",
                                background: cardBackground,
                                color: textColor,
                                fontSize: 12,
                                cursor: "pointer",
                                boxShadow: hoveredFlowSuggestion === flow.id ? chipShadowHover : chipShadowIdle,
                                transform: hoveredFlowSuggestion === flow.id ? "translateY(-1px)" : "translateY(0)",
                                transition: "box-shadow 0.18s ease, transform 0.18s ease",
                            }, children: flow.label }, flow.id))) })) : null, messageSuggestions.length > 0 && !inputDisabled ? (_jsx("div", { style: {
                            alignSelf: "flex-end",
                            display: "flex",
                            flexDirection: "column",
                            gap: 8,
                            alignItems: "flex-end",
                            maxWidth: "82%",
                            marginLeft: "auto",
                            marginTop: 4,
                        }, children: messageSuggestions.slice(0, 3).map((suggestion) => {
                            var _a, _b;
                            const text = suggestion.message;
                            const suggestionKey = `${text}-${(_a = suggestion.selectedPlaybookId) !== null && _a !== void 0 ? _a : ""}-${(_b = suggestion.currentStepId) !== null && _b !== void 0 ? _b : ""}`;
                            return (_jsx("button", { onClick: () => {
                                    var _a;
                                    onPickSuggestion(suggestion);
                                    (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                                }, onMouseEnter: () => setHoveredSuggestion(text), onMouseLeave: () => setHoveredSuggestion((prev) => (prev === text ? null : prev)), style: {
                                    borderRadius: 9999,
                                    border: "1px solid #d8e0eb",
                                    padding: "6px 10px",
                                    background: cardBackground,
                                    color: textColor,
                                    fontSize: 12,
                                    cursor: "pointer",
                                    boxShadow: hoveredSuggestion === text ? chipShadowHover : chipShadowIdle,
                                    transform: hoveredSuggestion === text ? "translateY(-1px)" : "translateY(0)",
                                    transition: "box-shadow 0.18s ease, transform 0.18s ease",
                                }, children: text }, suggestionKey));
                        }) })) : null, showConsent ? (_jsx(ConsentNotice, { accent: theme.accentColor, brandName: brandName, onApprove: onApproveConsent, contentColor: textColor, surfaceColor: surface, t: t })) : null, loading && (!streamingId || activityLabel) ? (_jsxs("div", { style: {
                            alignSelf: "flex-start",
                            color: textColor,
                            maxWidth: "60%",
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 10,
                        }, children: [_jsx("img", { src: resolveWidgetAssetUrl(agentImage) || resolveWidgetAssetUrl(DEFAULT_AGENT_IMAGE) || DEFAULT_AGENT_IMAGE, alt: brandName, style: { width: 24, height: 24, borderRadius: "50%", objectFit: "cover", flexShrink: 0, marginTop: 2 } }), activityLabel ? _jsx("div", { role: "status", "aria-live": "polite", style: { fontSize: 13, color: mutedColor, paddingTop: 3 }, children: activityLabel })
                                : _jsx(TypingDots, { color: theme.accentColor })] })) : null] }), _jsx("div", { style: {
                    padding: 14,
                    borderTop: "none",
                    background: theme.activeChatBackgroundColor || theme.background || "#f4f7fb",
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                    position: "relative",
                }, children: _jsxs("div", { style: {
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        borderRadius: 999,
                        border: "none",
                        background: cardBackground,
                        padding: "6px 8px",
                        boxShadow: inputShadow,
                    }, children: [_jsxs("div", { ref: emojiWrapRef, style: { position: "relative", flexShrink: 0 }, children: [_jsx("button", { type: "button", onClick: () => setEmojiOpen((open) => !open), style: {
                                        width: 24,
                                        height: 24,
                                        borderRadius: "50%",
                                        border: "none",
                                        background: cardBackground,
                                        color: textColor,
                                        cursor: inputDisabled ? "not-allowed" : "pointer",
                                        display: "grid",
                                        placeItems: "center",
                                        fontSize: 14,
                                        lineHeight: 1,
                                    }, disabled: inputDisabled, "aria-label": "Open emoji picker", children: "\uD83D\uDE42" }), emojiOpen && !inputDisabled ? (_jsx("div", { style: { position: "absolute", bottom: 36, left: 0, zIndex: 30 }, children: _jsx(EmojiMartPicker, { onEmojiSelect: (nativeEmoji) => {
                                            onInputChange(`${input}${nativeEmoji}`);
                                        }, previewPosition: "none", skinTonePosition: "none", theme: "light" }) })) : null] }), _jsx("input", { ref: inputRef, value: input, onChange: (e) => onInputChange(e.target.value), placeholder: t("chat.placeholder"), style: {
                                flex: 1,
                                padding: "8px 4px",
                                borderRadius: 999,
                                border: "none",
                                background: cardBackground,
                                color: textColor,
                                outline: "none",
                                fontSize: 14,
                            }, onKeyDown: (e) => {
                                if (e.key === "Enter") {
                                    if (e.repeat)
                                        return;
                                    e.preventDefault();
                                    onSend();
                                }
                            }, disabled: inputDisabled }), _jsx("button", { onClick: () => onSend(), style: {
                                width: 28,
                                height: 28,
                                borderRadius: "50%",
                                background: "transparent",
                                color: inputDisabled ? "#94a3b8" : "#312e81",
                                border: "none",
                                cursor: inputDisabled ? "not-allowed" : "pointer",
                                display: "grid",
                                placeItems: "center",
                                padding: 0,
                            }, disabled: inputDisabled, "aria-label": t("chat.send"), children: _jsx("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", "aria-hidden": true, children: _jsx("path", { d: "M4 12.5l15-7-4.5 13-3-4-4 2z", stroke: "currentColor", strokeWidth: "1.9", strokeLinecap: "round", strokeLinejoin: "round" }) }) })] }) })] }));
}
